<template>
  <div class="appointment__calendar-filters d-flex align-center">
    <!-- filtro por imóvel (busca assíncrona, múltiplo) -->
    <v-autocomplete
      v-model="selectedRealties"
      class="appointment__calendar-filter mr-2"
      :label="$t('appointment.formCreate.calendar.filters.realty')"
      :no-data-text="$t('appointment.filters.noData')"
      :items="realtyItems"
      :loading="isRealtyLoading"
      :search-input.sync="realtySearch"
      :item-text="realtyText"
      item-value="id"
      prepend-inner-icon="mdi-home-search-outline"
      multiple
      return-object
      hide-no-data
      hide-details
      clearable
      small-chips
      deletable-chips
      single-line
      outlined
      dense
    >
      <template v-slot:selection="{ item, index }">
        <v-chip
          v-if="index === 0"
          small
          close
          @click:close="selectedRealties = []"
        >
          <span class="text-truncate" style="max-width: 70px;">
            {{ realtyText(item) }}
          </span>
          <span v-if="selectedRealties.length > 1" class="ml-1">
            +{{ selectedRealties.length - 1 }}
          </span>
        </v-chip>
      </template>
    </v-autocomplete>

    <!-- filtro por trabalhador (lista agrupada: Hosts / Internos / Terceiros) -->
    <v-autocomplete
      v-model="localHostIds"
      class="appointment__calendar-filter mr-2"
      :label="$t('appointment.formCreate.calendar.filters.host')"
      :no-data-text="$t('appointment.filters.noData')"
      :items="workerItems"
      item-text="name"
      item-value="id"
      prepend-inner-icon="mdi-account-search-outline"
      multiple
      hide-no-data
      hide-details
      clearable
      small-chips
      deletable-chips
      single-line
      outlined
      dense
    >
      <template v-slot:selection="{ item, index }">
        <v-chip
          v-if="index === 0"
          small
          close
          @click:close="localHostIds = []"
        >
          <span class="text-truncate" style="max-width: 70px;">
            {{ item.name }}
          </span>
          <span v-if="localHostIds.length > 1" class="ml-1">
            +{{ localHostIds.length - 1 }}
          </span>
        </v-chip>
      </template>
    </v-autocomplete>

    <!-- modo de visualização: janela de 1 ou 3 dias -->
    <v-btn-toggle
      v-model="localDays"
      class="appointment__calendar-toggle flex-shrink-0"
      mandatory
      dense
      color="primary"
    >
      <v-btn :value="1" x-small>
        {{ $t('appointment.formCreate.calendar.oneDay') }}
      </v-btn>
      <v-btn :value="3" x-small>
        {{ $t('appointment.formCreate.calendar.threeDays') }}
      </v-btn>
    </v-btn-toggle>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  export default {
    name: 'AppointmentCalendarFilters',

    props: {
      daysInWindow: {
        type: Number,
        default: 3,
      },
      hostIds: {
        type: Array,
        default: () => [],
      },
      realtyIds: {
        type: Array,
        default: () => [],
      },
    },

    data () {
      return {
        selectedRealties: [],
        realtySearchResults: [],
        realtySearch: null,
        isRealtyLoading: false,
        realtyTimer: null,
      }
    },

    computed: {
      ...mapGetters(['workers']),

      // lista combinada com cabeçalhos por tipo (Hosts / Internos / Terceiros).
      // Vuetify renderiza { header } como subtítulo não-selecionável.
      workerItems () {
        const items = []
        const addGroup = (labelKey, list) => {
          if (!list || !list.length) return
          items.push({ header: this.$t(labelKey) })
          list.forEach((worker) => items.push(worker))
        }
        addGroup('appointment.formCreate.calendar.filters.groupHosts', this.workers.hosts)
        addGroup('appointment.formCreate.calendar.filters.groupInternals', this.workers.internals)
        addGroup('appointment.formCreate.calendar.filters.groupThirdParties', this.workers.thirdParties)
        return items
      },

      localDays: {
        get () {
          return this.daysInWindow
        },
        set (value) {
          this.$emit('update:daysInWindow', value)
        },
      },

      localHostIds: {
        get () {
          return this.hostIds
        },
        set (value) {
          this.$emit('update:hostIds', value)
        },
      },

      // itens do autocomplete de imóvel: resultados da busca + já selecionados
      // (evita que os chips selecionados sumam quando a busca muda)
      realtyItems () {
        const map = new Map()
        this.selectedRealties.forEach((realty) => map.set(realty.id, realty))
        this.realtySearchResults.forEach((realty) => map.set(realty.id, realty))
        return Array.from(map.values())
      },
    },

    watch: {
      realtySearch (value) {
        this.searchRealtyDebounced(value)
      },

      selectedRealties (realties) {
        this.$emit('update:realtyIds', realties.map((realty) => realty.id))
      },
    },

    created () {
      this.fetchWorkers()
    },

    methods: {
      ...mapActions(['fetchWorkers', 'searchRealties']),

      realtyText (realty) {
        return [realty.code, realty.address].filter(Boolean).join(' - ')
      },

      searchRealtyDebounced (searchText) {
        if (!searchText || searchText.length < 3) return

        clearTimeout(this.realtyTimer)
        this.isRealtyLoading = true

        this.realtyTimer = setTimeout(() => {
          this.searchRealties({ searchText })
            .then(({ data }) => {
              this.realtySearchResults = data || []
            })
            .finally(() => {
              this.isRealtyLoading = false
            })
        }, 500)
      },
    },
  }
</script>

<style scoped>
/* dois autocompletes dividem a largura e encolhem (min-width:0 permite truncar) */
.appointment__calendar-filter {
  flex: 1 1 0;
  min-width: 0;
}

/* mantém chip + input na mesma linha (evita o contador quebrar pra baixo) */
.appointment__calendar-filter >>> .v-select__selections {
  flex-wrap: nowrap;
}

.appointment__calendar-toggle >>> .v-btn {
  height: auto !important;
  min-width: 0 !important;
  padding: 8px 12px !important;
  font-size: 6px !important;
  letter-spacing: 0;
}
</style>
